<template>
    <section class="ink-about">
        <div class="ink-about__container container">
            <div class="ink-about__slider swiper-container swiper js-about-slider">
                <div class="swiper-wrapper">
                    <div
                        v-for="(loopSlide, index) in slides"
                        :key="loopSlide.id"
                        class="ink-about__slide swiper-slide"
                    >
                        <div class="ink-about__slide-media-wrapper">
                            <div
                                class="ink-about__slide-media"
                                v-html='renderMedia(loopSlide)'
                            >
                            </div>
                        </div>
                        <h3 class="ink-about__slide-title" v-html="loopSlide.title"/>
                        <p class="ink-about__slide-text" :class="loopSlide.text.length > 40 ? 'text-size-l' : 'text-size-xl'">{{ loopSlide.text }}</p>
                    </div>
                </div>
                <div class="ink-about__slider-controls">
                    <div class="ink-about__slider-pagination">
                        <div class="ink-about__slider-progress">
                            <span :style="`width: ${progressWidth}%`"></span>
                        </div>
                        <div class="ink-about__slider-count">
                            <span class="ink-about__slider-count-active">{{ activeSlideIndex }}</span>
                            <span class="ink-about__slider-count-all">/{{ countSlideLength }}</span>
                        </div>
                    </div>
                    <button
                        v-if="!isAutoplay"
                        type="button"
                        class="ink-about__slider-play"
                        @click="startAutoplay"
                    >
                        <z-icon
                            name="tools/play_m"
                            width="18"
                            height="18"
                        />
                    </button>
                    <button
                        v-else
                        type="button"
                        class="ink-about__slider-play"
                        @click="stopAutoplay"
                    >
                        <z-icon
                            name="tools/pause_m"
                            width="18"
                            height="18"
                        />
                    </button>
                </div>
            </div>

            <div class="ink-about__info">
                <h2 class="ink-about__title">{{ title }}</h2>
                <p class="ink-about__text" v-html="text"/>
            </div>
        </div>
    </section>
</template>
<script setup>
import {onMounted, ref, computed} from 'vue';
import Swiper from 'swiper/swiper-bundle.mjs'
import {localize} from '@/utils/i18n'

const props = defineProps({
    title: {
        type: String,
        default: localize({
            ru: '25 лет развиваем Восточную Сибирь',
            en: 'Developing Eastern Siberia for over 25 years',
            cn: '我们已在东西伯利亚地区开发超过25年。'
        }),
    },
    text: {
        type: String,
        default: localize({
            ru: 'ИНК&nbsp;&mdash;&nbsp;производитель и&nbsp;переработчик углеводородного сырья',
            en: 'INK is&nbsp;an&nbsp;producer and processor of&nbsp;hydrocarbons',
            cn: 'INK是俄罗斯一家独立的碳氢化合物原料生产商和加工商。'
        }),
    },
    slides: {
        type: Array,
        default: () => []
    }
})

const progressWidth = ref(0);
const activeSlideIndex = ref('01')
const aboutSwiper = ref();
let aboutSlider;

const renderMedia = (loopSlide) => {
    const videoTypes = ['mp4', 'mov', 'webm']

    const getVideoType = (src) => {
        const extension = src.split('.').pop();
        if (videoTypes.includes(extension)) {
            return `video/${extension}`;
        }
        return '';
    };

    if (typeof loopSlide.source === 'string') {
        const videoType = getVideoType(loopSlide.source);
        if (videoType) {
            return `<video autoplay muted loop playsinline><source src="${loopSlide.source}" type="${videoType}"></video>`;
        } else {
            return `<img src="${loopSlide.source}" alt="Image">`;
        }
    } else if (Array.isArray(loopSlide.source)) {
        let sources = loopSlide.source.map(src => {
            const videoType = getVideoType(src);
            if (videoType) {
                return `<source src="${src}" type="${videoType}">`;
            } else {
                return `<source srcset="${src}" type="image/jpeg">`;
            }
        });

        if (getVideoType(loopSlide.source[0])) {
            return `<video autoplay muted loop playsinline>${sources.join('')}</video>`;
        } else {
            return `<picture>${sources.join('')}<img src="${loopSlide.source[0]}" alt="Image"></picture>`;
        }
    } else {
        return 'Неверный формат данных в loopSlide.source';
    }
};

onMounted(() => {
    aboutSlider = document.querySelector('.js-about-slider')
    aboutSwiper.value = new Swiper(aboutSlider, {
        slidesPerView: 'auto',
        freeMode: true,
        spaceBetween: 24,
        speed: 400,
        grabCursor: true,
        loop: true,
        pagination: {
            el: '.swiper-pagination',
            type: 'progressbar',
        },
        autoplay: true,
        breakpoints: {
            1680: {
                spaceBetween: 100,
            },
            1360: {
                spaceBetween: 80,
            },
            1024: {
                spaceBetween: 48,
            },
        },
        on: {
            init: function () {
                activeSlideIndex.value = this.realIndex + 1 < 10 ? `0${this.realIndex + 1}` : `${this.realIndex + 1}`
                progressWidth.value = (this.realIndex + 1) / props.slides.length * 100
            },
            slideChange: function () {
                activeSlideIndex.value = this.realIndex + 1 < 10 ? `0${this.realIndex + 1}` : `${this.realIndex + 1}`
                progressWidth.value = (this.realIndex + 1) / props.slides.length * 100
            }
        }
    })
})

const isAutoplay = ref(false)

const startAutoplay = () => {
    isAutoplay.value = true

    return aboutSwiper.value?.autoplay.start()
}

const stopAutoplay = () => {
    isAutoplay.value = false

    return aboutSwiper.value?.autoplay.stop()
}

const countSlideLength = computed(() => {
    return props.slides.length < 10 ? `0${props.slides.length}` : props.slides.length
})

</script>
<style lang="scss">
.ink-about {
    position: relative;
    @include padding-level(top, 4XL);
    padding-bottom: calc(var(--spacer-4xl) * 2);
    background: url(/local/templates/z-template/images/about/adout-bg.jpg) lightgray 0px -0.531px / 100.521% 100.098% no-repeat;

    @include breakpoint(v-tablet) {
        padding-top: 0;
        min-height: unset;
    }

    &:before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(180deg, #035f50 15%, rgba(0, 0, 0, 0.5) 40%);
        opacity: 1;
        pointer-events: none;
    }

    &:after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(180deg, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0.00) 20.41%), #026958;
        opacity: 0.5;
        pointer-events: none;
    }

    &__container {
        position: relative;
        display: flex;
        justify-content: space-between;
        @include gap-level(3 XL);
        z-index: 2;

        @include breakpoint(v-tablet) {
            flex-direction: column-reverse;
            gap: 0;
        }
    }

    &__info {
        max-width: 39.4%;

        @include breakpoint(v-tablet) {
            @include padding-level(top, XL);
            @include padding-level(bottom, XL);
            max-width: 100%;
        }
    }

    &__title {
        @include margin-level(bottom, M);
        @include typo-level(2XL);
        font-weight: 500;
        line-height: 110%;
        color: var(--color-white);

        @include breakpoint(v-tablet) {
            @include margin-level(bottom, S);
            @include typo-level(3 XL);
        }
    }

    &__text {
        margin: 0;
        @include typo-level(XL);
        line-height: 140%;
        letter-spacing: 0.3px;
        color: var(--color-white);
    }

    &__slider {
        margin-left: -$token-grids-desktop-offset;
        margin-right: 0;
        @include padding-level(top, XL);
        @include padding-level(right, XL);
        @include padding-level(bottom, M);
        padding-left: $token-grids-desktop-offset;
        max-width: 59%;
        border-radius: 0 20px 20px 0;
        background: rgba(2, 105, 88, 0.86);
        border: 1px solid rgba(255, 255, 255, 0.20);
        backdrop-filter: blur(20px);

        @include breakpoint(laptop) {
            margin-left: -$token-grids-laptop-offset;
            padding-left: $token-grids-laptop-offset;
        }

        @include breakpoint(tablet) {
            margin-left: -$token-grids-tablet-offset;
            padding-left: $token-grids-tablet-offset;
        }

        @include breakpoint(v-tablet) {
            margin-left: -$token-grids-v-tablet-offset;
            padding-left: $token-grids-v-tablet-offset;
            max-width: calc(100% + $token-grids-v-tablet-offset);
        }

        @include breakpoint(mobile) {
            margin-left: -$token-grids-mobile-offset;
            padding-left: $token-grids-mobile-offset;
            max-width: calc(100% + $token-grids-mobile-offset);

        }

        & .swiper-wrapper {
            height: auto;
        }

        &-controls {
            display: flex;
            @include gap-level(XS);
            margin-top: var(--spacer-m);
        }

        &-play {
            @include padding-level(top, XS);
            @include padding-level(right, XS);
            @include padding-level(bottom, XS);
            @include padding-level(left, XS);
            display: flex;
            border-radius: 45px;
            border: unset;
            outline: none;
            background: var(--color-white);
        }

        &-pagination {
            width: 100%;
            display: flex;
            align-items: center;
            @include gap-level(S);
        }

        &-progress {
            position: relative;
            width: 100%;
            height: 1px;
            flex: 1 0 0;
            background: rgba(255, 255, 255, 0.80);

            & span {
                position: absolute;
                top: -2px;
                left: 0;
                width: 0;
                display: block;
                height: 5px;
                background: #FFF;
                transition: width .4s ease-in-out;
            }
        }

        &-count {
            &-active {
                color: rgba(255, 255, 255, 0.80);
                font-size: 28px;
                line-height: 160%;
                letter-spacing: 0.28px;
            }

            &-all {
                color: rgba(255, 255, 255, 0.80);
                @include typo-level(M);
                line-height: 160%;
                letter-spacing: 0.2px;
            }
        }
    }

    &__slide {
        width: 90%;
        max-width: 710px;

        @include breakpoint (tablet) {
            width: 85%;
        }

        @include breakpoint (mobile) {
            width: 80%;
        }

        &-title {
            margin: 16px 0 8px;
            font-size: 40px;
            font-weight: 500;
            line-height: 100%;
            letter-spacing: -0.8px;
            color: var(--color-white);
            @include breakpoint(v-tabet) {
                font-size: 24px;
            }
            @include breakpoint(mobile) {
                font-size: 18px;
            }

            & .number {
                font-size: 96px;
                font-weight: inherit;
                line-height: 100%;
                letter-spacing: -1.92px;

                @include breakpoint(laptop) {
                    font-size: 72px;
                }

                @include breakpoint(tabet) {
                    font-size: 56px;
                }

                @include breakpoint(v-tabet) {
                    font-size: 40px;
                }

                @include breakpoint(mobile) {
                    font-size: 32px;
                }
            }
        }

        &-text {
            margin: 0;
            max-width: 400px;
            // @include typo-level(XL);
            color: var(--color-white);
        }

        &-media {
            position: relative;
            padding-bottom: 48.902%;
            width: 79.85%;
            max-width: 567px;
            height: 0;
            border-radius: 20px;
            border: 1px solid rgba(255, 255, 255, 0.20);
            overflow: hidden;
            margin-left: 120px;

            @include breakpoint (laptop) {
                margin-left: 80px;
            }

            @include breakpoint (tablet) {
                margin-left: 64px;
            }

            @include breakpoint (v-tablet) {
                margin-left: 40px;
            }

            @include breakpoint (mobile) {
                margin-left: 16px;
            }

            &:before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: linear-gradient(180deg, rgba(23, 58, 29, 0.00) 60.71%, rgba(22, 55, 26, 0.30) 100%);
                pointer-events: none;
                z-index: 1;
            }

            & picture, & img, & video {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                display: block;
                object-fit: cover;
            }
        }
    }
}
</style>
